import React, { ReactElement, useState, useEffect, useRef } from 'react';
import './tag.less';
import {
  DATE_FORMAT_DD_MMMM_YYYY,
  DATE_FORMAT_DD_MMM_YYYY,
  DATE_PICKER_CONSTANTS,
  HIS_COMMON_DATE_FORMAT,
  TPA_BANNER_TEXT,
  UPC_TIME_FORMAT,
} from '../../constants/commonConstants';
import DatePicker from '../crmFormElement/FormInputDate/DatePicker';
import { ReactComponent as CalendarIcon } from '../../../assets/images/svg/CalendarIcon.svg';
import { ReactComponent as ChevronDown } from '../../../assets/images/svg/ChevronDown.svg';
import { customGTMEvent, setIsShowNow } from '../../../modules/crm/inPatient/service/crmUtils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { NewAdmissionState } from '../../../store/models/newAdmissionModel';
import { setDateApplied } from '../../../store/actions/newAdmissionAction';
import {
  setIsDateChanged,
  setPageIndex,
  setSelectedDateDropdown,
  setShowTpaEndDateAndTime,
  setShowTpaStartDateAndTime,
  setTpaGlobalSearch,
} from '../../../store/actions/inPatientAction';
import { combineDateAndTime } from '../../../utils/utility';
import {
  subDays,
  startOfDay,
  endOfDay,
  format,
  parse,
  differenceInDays,
  isToday,
} from 'date-fns';
import {ReactComponent as DateFilterIcon } from '../../../assets/images/svg/DateFilterIcon.svg';
import FilterDropdown from './genericMultiSelectDropdown';
import { DATE_DROPDOWN_FILTERS } from '../../constants/crmConstants';

const DateComponentTpa = (props: any): ReactElement => {
  const { showDateComponent } = props;
  const { isDateApplied } = useSelector(
    (state: RootState): NewAdmissionState => state.newAdmission,
  );
  const { showTpaStartDateAndTime, showTpaEndDateAndTime, isPreAuthView, selectedDateDropdown, tpaGlobalSearch } =
    useSelector((state: RootState): any => state.selectedInPatientDetail);
  const [showDatePicker, setShowDatePicker] = useState(showDateComponent);
  const [tpaStartDateTime, setTpaStartDateTime] = useState({
    date: showTpaStartDateAndTime?.date,
    time: showTpaStartDateAndTime?.time,
  });
  const [tpaEndDateTime, setTpaEndDateTime] = useState({
    date: showTpaEndDateAndTime?.date,
    time: showTpaEndDateAndTime?.time,
  });
  const datePickerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  let defaultStartDate: Date;
  let defaultEndDate: Date;

  useEffect((): void => {
    defaultStartDate = startOfDay(subDays(new Date(), 30)); // 30 days ago, at 00:00:00
    defaultEndDate = endOfDay(new Date());

    if (!tpaStartDateTime.date && !tpaEndDateTime.date) {
      setTpaStartDateTime({
        date: format(defaultStartDate, DATE_FORMAT_DD_MMM_YYYY),
        time: format(defaultStartDate, UPC_TIME_FORMAT),
      });
      setTpaEndDateTime({
        date: format(defaultEndDate, DATE_FORMAT_DD_MMM_YYYY),
        time: format(defaultEndDate, UPC_TIME_FORMAT),
      });
    }

    if (
      (!showTpaStartDateAndTime || !showTpaStartDateAndTime.date) &&
      (!showTpaEndDateAndTime || !showTpaEndDateAndTime.date)
    ) {
      dispatch(
        setShowTpaStartDateAndTime({
          date: format(defaultStartDate, DATE_FORMAT_DD_MMM_YYYY),
          time: format(defaultStartDate, UPC_TIME_FORMAT),
        }),
      );
      dispatch(
        setShowTpaEndDateAndTime({
          date: format(defaultEndDate, DATE_FORMAT_DD_MMM_YYYY),
          time: format(defaultEndDate, UPC_TIME_FORMAT),
        }),
      );
    }
  }, [isPreAuthView, props]);

  const handleSelectedFilters = (selectedFilters: string[]): void => {
    customGTMEvent({
      event: 'Selected TPA Date Filter',
      filters: selectedFilters,
    });
    dispatch(setSelectedDateDropdown(selectedFilters));
    dispatch(setPageIndex(1));
    dispatch(setTpaGlobalSearch({...tpaGlobalSearch, value: ''}));
  };

  const handleClickOutside = (event: MouseEvent): void => {
    const datePickerElement = datePickerRef.current;
    if (
      datePickerElement &&
      !datePickerElement.contains(event.target as HTMLElement) &&
      showDatePicker
    ) {
      setShowDatePicker(false);
      dispatch(setDateApplied(false));
    }
  };

  useEffect((): void => {
    if (isDateApplied) {
      dispatch(setShowTpaStartDateAndTime(tpaStartDateTime));
      dispatch(setShowTpaEndDateAndTime(tpaEndDateTime));
      dispatch(setIsDateChanged(true));
      dispatch(setTpaGlobalSearch({ ...tpaGlobalSearch, value: '' }));
    }
  }, [isDateApplied]);

  useEffect((): any => {
    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      dispatch(setIsDateChanged(false));
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const calculateDateDifference = (): number => {
    if (showTpaStartDateAndTime.date && showTpaEndDateAndTime.date) {
      const start = parse(
        showTpaStartDateAndTime.date,
        HIS_COMMON_DATE_FORMAT,
        new Date(),
      );
      const end = parse(
        showTpaEndDateAndTime.date,
        HIS_COMMON_DATE_FORMAT,
        new Date(),
      );
      return differenceInDays(end, start);
    }

    return TPA_BANNER_TEXT.DEFAULT_NO_OF_DAYS; // Default to 30 days if dates are not selected
  };

  const dateChangeHandler = (
    startDate: string,
    endDate: string,
    startTime: string,
    endTime: string,
  ): void => {
    dispatch(setDateApplied(false));

    if (startDate || startTime) {
      const parsedStartDate = startDate
        ? parse(startDate, DATE_FORMAT_DD_MMM_YYYY, new Date())
        : null;

      setTpaStartDateTime((prevState: any): any => ({
        ...prevState,
        ...(parsedStartDate && {
          date: format(parsedStartDate, HIS_COMMON_DATE_FORMAT),
        }),
        ...(startTime && { time: startTime }),
      }));
    }

    if (endDate || endTime) {
      const parsedEndDate = endDate
        ? parse(endDate, DATE_FORMAT_DD_MMM_YYYY, new Date())
        : null;
      setTpaEndDateTime((prevState: any): any => ({
        ...prevState,
        ...(parsedEndDate && {
          date: format(parsedEndDate, HIS_COMMON_DATE_FORMAT),
        }),
        ...(endTime && { time: endTime }),
      }));
    }
  };

  const checkShowNow = (): any => {
    return tpaStartDateTime?.date && tpaEndDateTime?.date
      ? setIsShowNow(
          new Date(showTpaStartDateAndTime?.date),
          new Date(showTpaStartDateAndTime?.date),
        )
      : true;
  };

  return (
    <div className="date-component-container">
      <span className="date-component-header">
        Currently showing{' '}
        <strong className="date-component-days">
          {TPA_BANNER_TEXT.LAST} {calculateDateDifference()}
          {TPA_BANNER_TEXT.DAYS}
        </strong>
        <span
          className="date-component-calendar"
          onClick={(): any => {
            customGTMEvent({
              event: 'Clicked on New TPA DatePicker',
            });
            setShowDatePicker(!showDatePicker);
          }}
        >
          <CalendarIcon style={{ marginRight: '5px' }} />
          <span className="tpa-date-color">
            {showTpaStartDateAndTime?.date && showTpaEndDateAndTime?.date
              ? `${
                  isToday(
                    parse(
                      showTpaStartDateAndTime?.date,
                      DATE_FORMAT_DD_MMMM_YYYY,
                      new Date(),
                    ),
                  )
                    ? 'Today'
                    : format(
                        parse(
                          showTpaStartDateAndTime?.date,
                          DATE_FORMAT_DD_MMMM_YYYY,
                          new Date(),
                        ),
                        DATE_FORMAT_DD_MMMM_YYYY,
                      )
                } - ${
                  isToday(
                    parse(
                      showTpaEndDateAndTime?.date,
                      DATE_FORMAT_DD_MMMM_YYYY,
                      new Date(),
                    ),
                  )
                    ? 'Today'
                    : format(
                        parse(
                          showTpaEndDateAndTime?.date,
                          DATE_FORMAT_DD_MMMM_YYYY,
                          new Date(),
                        ),
                        DATE_FORMAT_DD_MMMM_YYYY,
                      )
                }`
              : TPA_BANNER_TEXT.THIRTY_DAYS}
          </span>

          <ChevronDown />
        </span>
      </span>
      <div ref={datePickerRef} className="date-picker-container">
        {showDatePicker && (
          <DatePicker
            format={DATE_FORMAT_DD_MMM_YYYY}
            setIsDatePickerOpen={setShowDatePicker}
            initialAdmitDate={
              new Date(
                combineDateAndTime(
                  showTpaStartDateAndTime?.date,
                  showTpaStartDateAndTime?.time,
                ),
              )
            }
            initialDischargeDate={
              new Date(
                combineDateAndTime(
                  showTpaEndDateAndTime?.date,
                  showTpaEndDateAndTime?.time,
                ),
              )
            }
            allowClear={true}
            setSelect={dateChangeHandler}
            initialAdmitTime={format(
              parse(showTpaStartDateAndTime?.time, UPC_TIME_FORMAT, new Date()),
              UPC_TIME_FORMAT,
            )}
            initialDischargeTime={format(
              parse(showTpaEndDateAndTime?.time, UPC_TIME_FORMAT, new Date()),
              UPC_TIME_FORMAT,
            )}
            showNow={checkShowNow}
            disablefutureDates={true}
            isTpaDateSelector={true}
            doaLeftLimit={DATE_PICKER_CONSTANTS.ONE_YEAR_AGO}
          />
        )}
      </div>

      <FilterDropdown
        filters={DATE_DROPDOWN_FILTERS}
        onChange={handleSelectedFilters}
        selectedDropdownFilters={selectedDateDropdown}
        component={<DateFilterIcon className="mouse-pointer" />}
      />

      {selectedDateDropdown?.length > 0 && (
        <span className="date-filters-container">
          {selectedDateDropdown?.map((filter: string): any => (
            <span className="selected-date-filters" key={filter}>
              {filter}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

export default DateComponentTpa;
